import { AuctionNearMeHit } from '@/pages/AuctionsNearMe/types/AuctionsNearMe.types';
import { defaultHitsPerPage } from '@/pages/AuctionsNearMe/constants/AuctionsNearMeConstants';
import { fetchCatalogsIfNeeded } from '@/redux/modules/catalog/catalogs/catalog.actions';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { GeoArea } from '@/types/GeoArea';
import { ReactElement, useEffect, useState } from 'react';
import { useAppDispatch } from '@/redux/hooks';
import AuctionsNearMeFilterBar from '@/pages/AuctionsNearMe/components/MapView/components/CardsView/components/FilterBar/AuctionsNearMeFilterBar';
import CatalogGrid from '@/components/CatalogCard/CatalogGrid';
import Pagination from '@/components/Pagination/hammer-pagination/Pagination';
import RelatedGeoAreas from '@/components/RelatedGeoAreas/RelatedGeoAreas';
import styled from 'styled-components';
import Text from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import Throbber from '@liveauctioneers/caterwaul-components/lib/Throbber/Throbber';
import useDeepCompareEffect from 'use-deep-compare-effect';

export type AuctionsNearMeCardsViewProps = {
    geoArea: GeoArea;
    geocoderFailed: boolean;
    hits: AuctionNearMeHit[];
    setHasFilteredResults: (hasResults: boolean) => void;
    updateFilteredHits: (filteredHits: AuctionNearMeHit[]) => void;
};

const AuctionsNearMeCardsView = ({
    geoArea,
    geocoderFailed,
    hits,
    setHasFilteredResults,
    updateFilteredHits,
}: AuctionsNearMeCardsViewProps): ReactElement => {
    const dispatch = useAppDispatch();
    // Initialize loading state based on whether hits are available - same on server and client
    const [isLoading, setIsLoading] = useState(!hits || hits.length === 0);
    const [pageNumber, setPageNumber] = useState(1);
    const [perPage, setPerPage] = useState(defaultHitsPerPage);
    const [filteredHits, setFilteredHits] = useState(hits);
    const [keywords, setKeywords] = useState<string[]>([]);
    const [localPickUpFilter, setLocalPickUpFilter] = useState(false);
    const [topRatedFilter, setTopRatedFilter] = useState(false);

    const catalogIds = filteredHits
        .slice((pageNumber - 1) * perPage, pageNumber * perPage)
        .map((hit) => hit.Catalog.ID);

    useDeepCompareEffect(() => {
        const hitCatalogIds = filteredHits
            .slice((pageNumber - 1) * perPage, pageNumber * perPage)
            .map((hit) => hit.Catalog.ID);
        dispatch(fetchCatalogsIfNeeded({ catalogIds: hitCatalogIds }));
    }, [dispatch, filteredHits, pageNumber, perPage]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pageNumber]);

    const onPageChange = ({ page: newPageNumber, pageSize }) => {
        setPageNumber(newPageNumber);
        if (pageSize) {
            setPerPage(pageSize);
        }
    };

    useEffect(() => {
        if (hits.length > 0) {
            setIsLoading(false);
        }
        let newFilteredHits = hits;
        if (keywords.length > 0) {
            const keywordsRegex = new RegExp(keywords.join('|'), 'i');
            newFilteredHits = hits.filter((hit) => {
                const { Descr, Title } = hit.Catalog;
                return keywordsRegex.test(Title) || keywordsRegex.test(Descr);
            });
        }
        if (localPickUpFilter) {
            newFilteredHits = newFilteredHits.filter((hit) => hit.ShippingOptions?.HasFreeLocalPickup === 1);
        }
        if (topRatedFilter) {
            newFilteredHits = newFilteredHits.filter((hit) => hit.House.TopRated);
        }
        setFilteredHits(newFilteredHits);
    }, [hits, keywords, localPickUpFilter, topRatedFilter]);

    useEffect(() => {
        updateFilteredHits(filteredHits);
        setHasFilteredResults(filteredHits.length > 0);
    }, [filteredHits, updateFilteredHits, setHasFilteredResults]);

    const toggleLocalPickUpFilter = () => {
        setLocalPickUpFilter(!localPickUpFilter);
    };
    const toggleTopRatedFilter = () => {
        setTopRatedFilter(!topRatedFilter);
    };

    const hitsView = (
        <StyledCardsView>
            <Pagination
                onChange={onPageChange}
                paginationFilter={{
                    page: pageNumber,
                    pageSize: perPage,
                    totalRecords: filteredHits.length,
                }}
            >
                <StyledCatalogGrid catalogIds={catalogIds} />
            </Pagination>
        </StyledCardsView>
    );

    const noHitsView = (
        <section>
            <Text colorPrimaryWeak>
                <FormattedMessage id="no_auctions_available_in_area" />
            </Text>
        </section>
    );

    const hasNoResults = geocoderFailed || filteredHits.length === 0;

    return (
        <>
            {isLoading ? (
                <Throbber testid="auctions-near-me-cards-view-throbber" />
            ) : (
                <>
                    <StyledStickyHeader>
                        <AuctionsNearMeFilterBar
                            numberOfAuctions={hasNoResults ? 0 : filteredHits.length}
                            onKeywordsChange={setKeywords}
                            toggleLocalPickUpFilter={toggleLocalPickUpFilter}
                            toggleTopRatedFilter={toggleTopRatedFilter}
                        />
                    </StyledStickyHeader>
                    {hasNoResults ? noHitsView : hitsView}
                    <StyledRelatedGeoAreas>
                        <RelatedGeoAreas geoAreaId={geoArea?.geoAreaId || 0} />
                    </StyledRelatedGeoAreas>
                </>
            )}
        </>
    );
};

export default AuctionsNearMeCardsView;

const StyledCardsView = styled.section`
    [class^='paginationWrapper'] {
        margin-top: 20px;
    }
`;

const StyledCatalogGrid = styled(CatalogGrid).attrs(() => ({
    catalogCardClass: 'catalogCardClass',
}))`
    flex: 1 0 auto;
    margin: 0 -10px;
    width: calc(100% + 20px);
    max-width: calc(100% + 20px);
    min-height: 360px;

    && {
        .catalogCardClass {
            @media (max-width: 600px) {
                max-width: 100%;
                width: 100%;
            }
            @media (min-width: 601px) {
                max-width: calc(100% / 2);
                width: calc(100% / 2);
            }
        }
    }
`;

const StyledStickyHeader = styled.section`
    @media (min-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidthNum + 1}px) {
        position: sticky;
        top: 0;
        background-color: ${({ theme }) => theme.colors.white};
        z-index: 11;
        padding-top: 32px;
        padding-bottom: 16px;
    }
`;

const StyledRelatedGeoAreas = styled.section`
    margin-top: 64px;
`;
